import React, { useState, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import MedicalInformationIcon from "@mui/icons-material/MedicalInformation";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Circles } from "react-loader-spinner";
import baseUrl from "../../../../../api/baseUrl";

interface PatientViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  patientData: any;
  loading: boolean;
  theme?: "light" | "dark";
  onEdit?: (id: string, e: React.MouseEvent) => void;
  onAddTreatment?: (id: string, e: React.MouseEvent) => void;
  onDelete?: (id: string, e: React.MouseEvent) => void;
  canDelete?: boolean;
}

const formatDate = (dateString?: string) => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleDateString();
};

const formatAddress = (address: any) => {
  if (typeof address === "object" && address !== null) {
    return `${address.street || ""} ${address.no || ""}, ${address.city || ""}, ${
      address.district || ""
    }, ${address.province || ""}, ${address.country || ""}`.trim() || "N/A";
  }
  return address || "N/A";
};

const PatientViewModal: React.FC<PatientViewModalProps> = ({
  isOpen,
  onClose,
  patientData,
  loading,
  theme = "light",
  onEdit,
  onAddTreatment,
  onDelete,
  canDelete = false,
}) => {
  const [activeTab, setActiveTab] = useState<"general" | "medical" | "examination" | "invoices">("general");
  const [examinationData, setExaminationData] = useState<any[]>([]);
  const [invoicesData, setInvoicesData] = useState<any[]>([]);
  const [loadingExamination, setLoadingExamination] = useState(false);
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  useEffect(() => {
    if (isOpen && patientData?._id) {
      // Fetch examination data
      setLoadingExamination(true);
      baseUrl
        .get("/patient/findTreatmentByPid", {
          headers: {
            patientid: patientData._id,
          },
        })
        .then((response: any) => {
          setExaminationData(response.data.data || []);
        })
        .catch((e: any) => {
          console.log(e);
          setExaminationData([]);
        })
        .finally(() => {
          setLoadingExamination(false);
        });

      // Fetch invoices data
      setLoadingInvoices(true);
      baseUrl
        .get("/payment/getPaymentByPatient", {
          headers: {
            patientid: patientData._id,
          },
        })
        .then((response: any) => {
          setInvoicesData(response.data.data || []);
        })
        .catch((e: any) => {
          console.log(e);
          setInvoicesData([]);
        })
        .finally(() => {
          setLoadingInvoices(false);
        });
    }
  }, [isOpen, patientData?._id]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center px-4 pb-4" style={{ margin: 0, top: 0, paddingTop: 0 }} onClick={onClose}>
      <div className={`rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col transition-colors ${
        theme === "dark" ? "bg-slate-800" : "bg-white"
      }`} style={{ margin: 0 }} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={`px-6 py-4 border-b flex items-center justify-between ${
          theme === "dark" 
            ? "border-slate-700 bg-gradient-to-r from-indigo-600 to-indigo-700" 
            : "border-slate-100 bg-gradient-to-r from-primary to-primaryLight"
        }`}>
          <h2 className="text-xl font-bold text-white">Patient Summary</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors"
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Circles height="40" width="40" color="#6366f1" ariaLabel="loading" />
            </div>
          ) : patientData ? (
            <>
              {/* Profile Header */}
              <div className="flex items-start justify-between gap-4 mb-6">
                <div className="flex items-start gap-4 flex-1">
                  {patientData?.image ? (
                    <img src={patientData.image} alt={patientData.fullName} className="w-16 h-16 rounded-xl object-cover" />
                  ) : (
                    <div className={`w-16 h-16 rounded-xl flex items-center justify-center ${
                      theme === "dark" 
                        ? "bg-gradient-to-br from-indigo-600 to-indigo-700" 
                        : "bg-gradient-to-br from-primary to-primaryLight"
                    }`}>
                      <PersonIcon className="text-white" sx={{ fontSize: 32 }} />
                    </div>
                  )}
                  <div className="flex-1">
                    <h3 className={`text-lg font-bold mb-1 ${theme === "dark" ? "text-slate-200" : "text-slate-800"}`}>
                      {`${patientData?.title || ""} ${patientData?.fullName || "N/A"}`}
                    </h3>
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        theme === "dark"
                          ? "bg-indigo-900/30 text-indigo-300"
                          : "bg-indigo-100 text-indigo-700"
                      }`}>
                        {patientData?.patientType || "Normal"} Patient
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-3 text-xs">
                      <div className={`${theme === "dark" ? "text-slate-400" : "text-slate-600"}`}>
                        <span className="font-medium">Reg No: </span>
                        <span className={theme === "dark" ? "text-slate-300" : "text-slate-700"}>
                          {patientData?.regNo || "N/A"}
                        </span>
                      </div>
                      <div className={`${theme === "dark" ? "text-slate-400" : "text-slate-600"}`}>
                        <span className="font-medium">Mobile: </span>
                        <span className={theme === "dark" ? "text-slate-300" : "text-slate-700"}>
                          {patientData?.mobileNo || "N/A"}
                        </span>
                      </div>
                      <div className={`${theme === "dark" ? "text-slate-400" : "text-slate-600"}`}>
                        <span className="font-medium">Gender: </span>
                        <span className={theme === "dark" ? "text-slate-300" : "text-slate-700"}>
                          {patientData?.gender || "N/A"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {onEdit && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (patientData?._id && onEdit) {
                          onEdit(patientData._id, e);
                          onClose();
                        }
                      }}
                      className={`p-1.5 rounded-lg transition-colors ${
                        theme === "dark"
                          ? "bg-amber-900/30 text-amber-300 hover:bg-amber-900/50"
                          : "bg-amber-50 text-amber-600 hover:bg-amber-100"
                      }`}
                      title="Edit"
                    >
                      <BorderColorIcon sx={{ fontSize: 16 }} />
                    </button>
                  )}
                  {onAddTreatment && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (patientData?._id && onAddTreatment) {
                          onAddTreatment(patientData._id, e);
                          onClose();
                        }
                      }}
                      className={`p-1.5 rounded-lg transition-colors ${
                        theme === "dark"
                          ? "bg-emerald-900/30 text-emerald-300 hover:bg-emerald-900/50"
                          : "bg-emerald-50 text-emerald-600 hover:bg-emerald-100"
                      }`}
                      title="Add Treatment"
                    >
                      <MedicalInformationIcon sx={{ fontSize: 16 }} />
                    </button>
                  )}
                  {onDelete && patientData?.hasid && canDelete && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (patientData?._id && onDelete) {
                          onDelete(patientData._id, e);
                          onClose();
                        }
                      }}
                      className={`p-1.5 rounded-lg transition-colors ${
                        theme === "dark"
                          ? "bg-red-900/30 text-red-400 hover:bg-red-900/50"
                          : "bg-red-50 text-red-500 hover:bg-red-100"
                      }`}
                      title="Delete"
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                    </button>
                  )}
                </div>
              </div>

              {/* Tabs */}
              <div className={`flex gap-2 mb-6 p-1 rounded-lg ${
                theme === "dark" ? "bg-slate-700" : "bg-slate-100"
              }`}>
                <button
                  onClick={() => setActiveTab("general")}
                  className={`flex-1 py-2 px-2 rounded-md text-xs font-medium transition-all ${
                    activeTab === "general"
                      ? theme === "dark"
                        ? "bg-indigo-600 text-white shadow-sm"
                        : "bg-white text-indigo-600 shadow-sm"
                      : theme === "dark"
                      ? "text-slate-300 hover:text-slate-100"
                      : "text-slate-600 hover:text-slate-800"
                  }`}
                >
                  General Details
                </button>
                <button
                  onClick={() => setActiveTab("medical")}
                  className={`flex-1 py-2 px-2 rounded-md text-xs font-medium transition-all ${
                    activeTab === "medical"
                      ? theme === "dark"
                        ? "bg-indigo-600 text-white shadow-sm"
                        : "bg-white text-indigo-600 shadow-sm"
                      : theme === "dark"
                      ? "text-slate-300 hover:text-slate-100"
                      : "text-slate-600 hover:text-slate-800"
                  }`}
                >
                  Medical History
                </button>
                <button
                  onClick={() => setActiveTab("examination")}
                  className={`flex-1 py-2 px-2 rounded-md text-xs font-medium transition-all ${
                    activeTab === "examination"
                      ? theme === "dark"
                        ? "bg-indigo-600 text-white shadow-sm"
                        : "bg-white text-indigo-600 shadow-sm"
                      : theme === "dark"
                      ? "text-slate-300 hover:text-slate-100"
                      : "text-slate-600 hover:text-slate-800"
                  }`}
                >
                  Examination
                </button>
                <button
                  onClick={() => setActiveTab("invoices")}
                  className={`flex-1 py-2 px-2 rounded-md text-xs font-medium transition-all ${
                    activeTab === "invoices"
                      ? theme === "dark"
                        ? "bg-indigo-600 text-white shadow-sm"
                        : "bg-white text-indigo-600 shadow-sm"
                      : theme === "dark"
                      ? "text-slate-300 hover:text-slate-100"
                      : "text-slate-600 hover:text-slate-800"
                  }`}
                >
                  Invoices
                </button>
              </div>

              {/* Tab Content */}
              {activeTab === "general" && (
                <div className="space-y-6">
                  <div>
                    <h4 className={`text-sm font-semibold mb-3 ${
                      theme === "dark" ? "text-slate-300" : "text-slate-700"
                    }`}>Personal Information</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      <div className={`rounded-lg p-3 ${
                        theme === "dark" ? "bg-slate-700/50" : "bg-slate-50"
                      }`}>
                        <p className={`text-xs uppercase tracking-wide mb-1 ${
                          theme === "dark" ? "text-slate-400" : "text-slate-500"
                        }`}>Registered Date</p>
                        <p className={`text-sm font-medium ${
                          theme === "dark" ? "text-slate-200" : "text-slate-800"
                        }`}>{formatDate(patientData?.registerdDate)}</p>
                      </div>
                      <div className={`rounded-lg p-3 ${
                        theme === "dark" ? "bg-slate-700/50" : "bg-slate-50"
                      }`}>
                        <p className={`text-xs uppercase tracking-wide mb-1 ${
                          theme === "dark" ? "text-slate-400" : "text-slate-500"
                        }`}>Reg Number</p>
                        <p className={`text-sm font-medium ${
                          theme === "dark" ? "text-slate-200" : "text-slate-800"
                        }`}>{patientData?.regNo || "N/A"}</p>
                      </div>
                      <div className={`rounded-lg p-3 ${
                        theme === "dark" ? "bg-slate-700/50" : "bg-slate-50"
                      }`}>
                        <p className={`text-xs uppercase tracking-wide mb-1 ${
                          theme === "dark" ? "text-slate-400" : "text-slate-500"
                        }`}>Gender</p>
                        <p className={`text-sm font-medium ${
                          theme === "dark" ? "text-slate-200" : "text-slate-800"
                        }`}>{patientData?.gender || "N/A"}</p>
                      </div>
                      <div className={`rounded-lg p-3 ${
                        theme === "dark" ? "bg-slate-700/50" : "bg-slate-50"
                      }`}>
                        <p className={`text-xs uppercase tracking-wide mb-1 ${
                          theme === "dark" ? "text-slate-400" : "text-slate-500"
                        }`}>Date of Birth</p>
                        <p className={`text-sm font-medium ${
                          theme === "dark" ? "text-slate-200" : "text-slate-800"
                        }`}>{formatDate(patientData?.dob)}</p>
                      </div>
                      <div className={`rounded-lg p-3 ${
                        theme === "dark" ? "bg-slate-700/50" : "bg-slate-50"
                      }`}>
                        <p className={`text-xs uppercase tracking-wide mb-1 ${
                          theme === "dark" ? "text-slate-400" : "text-slate-500"
                        }`}>NIC</p>
                        <p className={`text-sm font-medium ${
                          theme === "dark" ? "text-slate-200" : "text-slate-800"
                        }`}>{patientData?.nic || "N/A"}</p>
                      </div>
                      <div className={`rounded-lg p-3 ${
                        theme === "dark" ? "bg-slate-700/50" : "bg-slate-50"
                      }`}>
                        <p className={`text-xs uppercase tracking-wide mb-1 ${
                          theme === "dark" ? "text-slate-400" : "text-slate-500"
                        }`}>Marital Status</p>
                        <p className={`text-sm font-medium ${
                          theme === "dark" ? "text-slate-200" : "text-slate-800"
                        }`}>{patientData?.maritalStatus || "N/A"}</p>
                      </div>
                      <div className={`rounded-lg p-3 ${
                        theme === "dark" ? "bg-slate-700/50" : "bg-slate-50"
                      }`}>
                        <p className={`text-xs uppercase tracking-wide mb-1 ${
                          theme === "dark" ? "text-slate-400" : "text-slate-500"
                        }`}>Religion</p>
                        <p className={`text-sm font-medium ${
                          theme === "dark" ? "text-slate-200" : "text-slate-800"
                        }`}>{patientData?.religion || "N/A"}</p>
                      </div>
                      <div className={`rounded-lg p-3 ${
                        theme === "dark" ? "bg-slate-700/50" : "bg-slate-50"
                      }`}>
                        <p className={`text-xs uppercase tracking-wide mb-1 ${
                          theme === "dark" ? "text-slate-400" : "text-slate-500"
                        }`}>Occupation</p>
                        <p className={`text-sm font-medium ${
                          theme === "dark" ? "text-slate-200" : "text-slate-800"
                        }`}>{patientData?.occupation || "N/A"}</p>
                      </div>
                      <div className={`rounded-lg p-3 ${
                        theme === "dark" ? "bg-slate-700/50" : "bg-slate-50"
                      }`}>
                        <p className={`text-xs uppercase tracking-wide mb-1 ${
                          theme === "dark" ? "text-slate-400" : "text-slate-500"
                        }`}>Blood Group</p>
                        <p className={`text-sm font-medium ${
                          theme === "dark" ? "text-slate-200" : "text-slate-800"
                        }`}>{patientData?.bloodGroup || "N/A"}</p>
                      </div>
                      <div className={`rounded-lg p-3 ${
                        theme === "dark" ? "bg-slate-700/50" : "bg-slate-50"
                      }`}>
                        <p className={`text-xs uppercase tracking-wide mb-1 ${
                          theme === "dark" ? "text-slate-400" : "text-slate-500"
                        }`}>Introduction</p>
                        <p className={`text-sm font-medium ${
                          theme === "dark" ? "text-slate-200" : "text-slate-800"
                        }`}>{patientData?.nameOfIntroduce || "N/A"}</p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className={`text-sm font-semibold mb-3 ${
                      theme === "dark" ? "text-slate-300" : "text-slate-700"
                    }`}>Contact Information</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      <div className={`rounded-lg p-3 ${
                        theme === "dark" ? "bg-slate-700/50" : "bg-slate-50"
                      }`}>
                        <p className={`text-xs uppercase tracking-wide mb-1 ${
                          theme === "dark" ? "text-slate-400" : "text-slate-500"
                        }`}>Mobile No</p>
                        <p className={`text-sm font-medium ${
                          theme === "dark" ? "text-slate-200" : "text-slate-800"
                        }`}>{patientData?.mobileNo || "N/A"}</p>
                      </div>
                      <div className={`rounded-lg p-3 ${
                        theme === "dark" ? "bg-slate-700/50" : "bg-slate-50"
                      }`}>
                        <p className={`text-xs uppercase tracking-wide mb-1 ${
                          theme === "dark" ? "text-slate-400" : "text-slate-500"
                        }`}>WhatsApp</p>
                        <p className={`text-sm font-medium ${
                          theme === "dark" ? "text-slate-200" : "text-slate-800"
                        }`}>{patientData?.whatsapp || "N/A"}</p>
                      </div>
                      <div className={`rounded-lg p-3 ${
                        theme === "dark" ? "bg-slate-700/50" : "bg-slate-50"
                      }`}>
                        <p className={`text-xs uppercase tracking-wide mb-1 ${
                          theme === "dark" ? "text-slate-400" : "text-slate-500"
                        }`}>Secondary No</p>
                        <p className={`text-sm font-medium ${
                          theme === "dark" ? "text-slate-200" : "text-slate-800"
                        }`}>{patientData?.smobileNo || "N/A"}</p>
                      </div>
                      <div className={`rounded-lg p-3 ${
                        theme === "dark" ? "bg-slate-700/50" : "bg-slate-50"
                      }`}>
                        <p className={`text-xs uppercase tracking-wide mb-1 ${
                          theme === "dark" ? "text-slate-400" : "text-slate-500"
                        }`}>Email</p>
                        <p className={`text-sm font-medium ${
                          theme === "dark" ? "text-slate-200" : "text-slate-800"
                        }`}>{patientData?.email || "N/A"}</p>
                      </div>
                      <div className="col-span-2">
                        <div className={`rounded-lg p-3 ${
                          theme === "dark" ? "bg-slate-700/50" : "bg-slate-50"
                        }`}>
                          <p className={`text-xs uppercase tracking-wide mb-1 ${
                            theme === "dark" ? "text-slate-400" : "text-slate-500"
                          }`}>Address</p>
                          <p className={`text-sm font-medium ${
                            theme === "dark" ? "text-slate-200" : "text-slate-800"
                          }`}>{formatAddress(patientData?.address)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "medical" && (
                <div className="space-y-6">
                  <div>
                    <h4 className={`text-sm font-semibold mb-3 ${
                      theme === "dark" ? "text-slate-300" : "text-slate-700"
                    }`}>Medical Conditions</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      <div className={`rounded-lg p-3 ${
                        patientData?.allergies?.status === "true"
                          ? theme === "dark"
                            ? "bg-red-900/30 border border-red-800"
                            : "bg-red-50 border border-red-100"
                          : theme === "dark"
                          ? "bg-slate-700/50"
                          : "bg-slate-50"
                      }`}>
                        <div className="flex items-center justify-between">
                          <p className={`text-sm font-medium ${
                            theme === "dark" ? "text-slate-300" : "text-slate-700"
                          }`}>Allergies</p>
                          {patientData?.allergies?.status === "true" ? (
                            <CheckCircleIcon className={theme === "dark" ? "text-red-400" : "text-red-500"} sx={{ fontSize: 18 }} />
                          ) : (
                            <CancelIcon className={theme === "dark" ? "text-slate-500" : "text-slate-300"} sx={{ fontSize: 18 }} />
                          )}
                        </div>
                        {patientData?.allergies?.status === "true" && patientData?.allergies?.note && (
                          <p className={`text-xs mt-1 ${
                            theme === "dark" ? "text-red-300" : "text-red-600"
                          }`}>Note: {patientData.allergies.note}</p>
                        )}
                      </div>
                      <div className={`rounded-lg p-3 ${
                        patientData?.gastricProblem?.status === "true"
                          ? theme === "dark"
                            ? "bg-red-900/30 border border-red-800"
                            : "bg-red-50 border border-red-100"
                          : theme === "dark"
                          ? "bg-slate-700/50"
                          : "bg-slate-50"
                      }`}>
                        <div className="flex items-center justify-between">
                          <p className={`text-sm font-medium ${
                            theme === "dark" ? "text-slate-300" : "text-slate-700"
                          }`}>Gastric Problem</p>
                          {patientData?.gastricProblem?.status === "true" ? (
                            <CheckCircleIcon className={theme === "dark" ? "text-red-400" : "text-red-500"} sx={{ fontSize: 18 }} />
                          ) : (
                            <CancelIcon className={theme === "dark" ? "text-slate-500" : "text-slate-300"} sx={{ fontSize: 18 }} />
                          )}
                        </div>
                        {patientData?.gastricProblem?.status === "true" && patientData?.gastricProblem?.note && (
                          <p className={`text-xs mt-1 ${
                            theme === "dark" ? "text-red-300" : "text-red-600"
                          }`}>Note: {patientData.gastricProblem.note}</p>
                        )}
                      </div>
                      <div className={`rounded-lg p-3 ${
                        patientData?.highBP?.status === "true"
                          ? theme === "dark"
                            ? "bg-red-900/30 border border-red-800"
                            : "bg-red-50 border border-red-100"
                          : theme === "dark"
                          ? "bg-slate-700/50"
                          : "bg-slate-50"
                      }`}>
                        <div className="flex items-center justify-between">
                          <p className={`text-sm font-medium ${
                            theme === "dark" ? "text-slate-300" : "text-slate-700"
                          }`}>High BP</p>
                          {patientData?.highBP?.status === "true" ? (
                            <CheckCircleIcon className={theme === "dark" ? "text-red-400" : "text-red-500"} sx={{ fontSize: 18 }} />
                          ) : (
                            <CancelIcon className={theme === "dark" ? "text-slate-500" : "text-slate-300"} sx={{ fontSize: 18 }} />
                          )}
                        </div>
                        {patientData?.highBP?.status === "true" && patientData?.highBP?.note && (
                          <p className={`text-xs mt-1 ${
                            theme === "dark" ? "text-red-300" : "text-red-600"
                          }`}>Note: {patientData.highBP.note}</p>
                        )}
                      </div>
                      <div className={`rounded-lg p-3 ${
                        patientData?.diabetes?.status === "true"
                          ? theme === "dark"
                            ? "bg-red-900/30 border border-red-800"
                            : "bg-red-50 border border-red-100"
                          : theme === "dark"
                          ? "bg-slate-700/50"
                          : "bg-slate-50"
                      }`}>
                        <div className="flex items-center justify-between">
                          <p className={`text-sm font-medium ${
                            theme === "dark" ? "text-slate-300" : "text-slate-700"
                          }`}>Diabetes</p>
                          {patientData?.diabetes?.status === "true" ? (
                            <CheckCircleIcon className={theme === "dark" ? "text-red-400" : "text-red-500"} sx={{ fontSize: 18 }} />
                          ) : (
                            <CancelIcon className={theme === "dark" ? "text-slate-500" : "text-slate-300"} sx={{ fontSize: 18 }} />
                          )}
                        </div>
                        {patientData?.diabetes?.status === "true" && patientData?.diabetes?.note && (
                          <p className={`text-xs mt-1 ${
                            theme === "dark" ? "text-red-300" : "text-red-600"
                          }`}>Note: {patientData.diabetes.note}</p>
                        )}
                      </div>
                      <div className={`rounded-lg p-3 ${
                        patientData?.monthlyInjection?.status === "true"
                          ? theme === "dark"
                            ? "bg-red-900/30 border border-red-800"
                            : "bg-red-50 border border-red-100"
                          : theme === "dark"
                          ? "bg-slate-700/50"
                          : "bg-slate-50"
                      }`}>
                        <div className="flex items-center justify-between">
                          <p className={`text-sm font-medium ${
                            theme === "dark" ? "text-slate-300" : "text-slate-700"
                          }`}>Monthly Injection</p>
                          {patientData?.monthlyInjection?.status === "true" ? (
                            <CheckCircleIcon className={theme === "dark" ? "text-red-400" : "text-red-500"} sx={{ fontSize: 18 }} />
                          ) : (
                            <CancelIcon className={theme === "dark" ? "text-slate-500" : "text-slate-300"} sx={{ fontSize: 18 }} />
                          )}
                        </div>
                        {patientData?.monthlyInjection?.status === "true" && patientData?.monthlyInjection?.note && (
                          <p className={`text-xs mt-1 ${
                            theme === "dark" ? "text-red-300" : "text-red-600"
                          }`}>Note: {patientData.monthlyInjection.note}</p>
                        )}
                      </div>
                      <div className={`rounded-lg p-3 ${
                        patientData?.pregnancyBreastFeeding?.status === "true"
                          ? theme === "dark"
                            ? "bg-red-900/30 border border-red-800"
                            : "bg-red-50 border border-red-100"
                          : theme === "dark"
                          ? "bg-slate-700/50"
                          : "bg-slate-50"
                      }`}>
                        <div className="flex items-center justify-between">
                          <p className={`text-sm font-medium ${
                            theme === "dark" ? "text-slate-300" : "text-slate-700"
                          }`}>Pregnancy/Breast Feeding</p>
                          {patientData?.pregnancyBreastFeeding?.status === "true" ? (
                            <CheckCircleIcon className={theme === "dark" ? "text-red-400" : "text-red-500"} sx={{ fontSize: 18 }} />
                          ) : (
                            <CancelIcon className={theme === "dark" ? "text-slate-500" : "text-slate-300"} sx={{ fontSize: 18 }} />
                          )}
                        </div>
                        {patientData?.pregnancyBreastFeeding?.status === "true" && patientData?.pregnancyBreastFeeding?.note && (
                          <p className={`text-xs mt-1 ${
                            theme === "dark" ? "text-red-300" : "text-red-600"
                          }`}>Note: {patientData.pregnancyBreastFeeding.note}</p>
                        )}
                      </div>
                      <div className={`rounded-lg p-3 ${
                        patientData?.coughColdFever?.status === "true"
                          ? theme === "dark"
                            ? "bg-red-900/30 border border-red-800"
                            : "bg-red-50 border border-red-100"
                          : theme === "dark"
                          ? "bg-slate-700/50"
                          : "bg-slate-50"
                      }`}>
                        <div className="flex items-center justify-between">
                          <p className={`text-sm font-medium ${
                            theme === "dark" ? "text-slate-300" : "text-slate-700"
                          }`}>Cough/Cold/Fever</p>
                          {patientData?.coughColdFever?.status === "true" ? (
                            <CheckCircleIcon className={theme === "dark" ? "text-red-400" : "text-red-500"} sx={{ fontSize: 18 }} />
                          ) : (
                            <CancelIcon className={theme === "dark" ? "text-slate-500" : "text-slate-300"} sx={{ fontSize: 18 }} />
                          )}
                        </div>
                        {patientData?.coughColdFever?.status === "true" && patientData?.coughColdFever?.note && (
                          <p className={`text-xs mt-1 ${
                            theme === "dark" ? "text-red-300" : "text-red-600"
                          }`}>Note: {patientData.coughColdFever.note}</p>
                        )}
                      </div>
                      <div className={`rounded-lg p-3 ${
                        patientData?.chestpainHeartProblem?.status === "true"
                          ? theme === "dark"
                            ? "bg-red-900/30 border border-red-800"
                            : "bg-red-50 border border-red-100"
                          : theme === "dark"
                          ? "bg-slate-700/50"
                          : "bg-slate-50"
                      }`}>
                        <div className="flex items-center justify-between">
                          <p className={`text-sm font-medium ${
                            theme === "dark" ? "text-slate-300" : "text-slate-700"
                          }`}>Chest Pain/Heart</p>
                          {patientData?.chestpainHeartProblem?.status === "true" ? (
                            <CheckCircleIcon className={theme === "dark" ? "text-red-400" : "text-red-500"} sx={{ fontSize: 18 }} />
                          ) : (
                            <CancelIcon className={theme === "dark" ? "text-slate-500" : "text-slate-300"} sx={{ fontSize: 18 }} />
                          )}
                        </div>
                        {patientData?.chestpainHeartProblem?.status === "true" && patientData?.chestpainHeartProblem?.note && (
                          <p className={`text-xs mt-1 ${
                            theme === "dark" ? "text-red-300" : "text-red-600"
                          }`}>Note: {patientData.chestpainHeartProblem.note}</p>
                        )}
                      </div>
                      <div className={`rounded-lg p-3 ${
                        patientData?.epilepsy?.status === "true"
                          ? theme === "dark"
                            ? "bg-red-900/30 border border-red-800"
                            : "bg-red-50 border border-red-100"
                          : theme === "dark"
                          ? "bg-slate-700/50"
                          : "bg-slate-50"
                      }`}>
                        <div className="flex items-center justify-between">
                          <p className={`text-sm font-medium ${
                            theme === "dark" ? "text-slate-300" : "text-slate-700"
                          }`}>Epilepsy</p>
                          {patientData?.epilepsy?.status === "true" ? (
                            <CheckCircleIcon className={theme === "dark" ? "text-red-400" : "text-red-500"} sx={{ fontSize: 18 }} />
                          ) : (
                            <CancelIcon className={theme === "dark" ? "text-slate-500" : "text-slate-300"} sx={{ fontSize: 18 }} />
                          )}
                        </div>
                        {patientData?.epilepsy?.status === "true" && patientData?.epilepsy?.note && (
                          <p className={`text-xs mt-1 ${
                            theme === "dark" ? "text-red-300" : "text-red-600"
                          }`}>Note: {patientData.epilepsy.note}</p>
                        )}
                      </div>
                      <div className={`rounded-lg p-3 ${
                        patientData?.previousExtraction?.status === "true"
                          ? theme === "dark"
                            ? "bg-red-900/30 border border-red-800"
                            : "bg-red-50 border border-red-100"
                          : theme === "dark"
                          ? "bg-slate-700/50"
                          : "bg-slate-50"
                      }`}>
                        <div className="flex items-center justify-between">
                          <p className={`text-sm font-medium ${
                            theme === "dark" ? "text-slate-300" : "text-slate-700"
                          }`}>Previous Extraction</p>
                          {patientData?.previousExtraction?.status === "true" ? (
                            <CheckCircleIcon className={theme === "dark" ? "text-red-400" : "text-red-500"} sx={{ fontSize: 18 }} />
                          ) : (
                            <CancelIcon className={theme === "dark" ? "text-slate-500" : "text-slate-300"} sx={{ fontSize: 18 }} />
                          )}
                        </div>
                        {patientData?.previousExtraction?.status === "true" && patientData?.previousExtraction?.note && (
                          <p className={`text-xs mt-1 ${
                            theme === "dark" ? "text-red-300" : "text-red-600"
                          }`}>Note: {patientData.previousExtraction.note}</p>
                        )}
                      </div>
                      <div className={`rounded-lg p-3 ${
                        patientData?.previoussurgeries?.status === "true"
                          ? theme === "dark"
                            ? "bg-red-900/30 border border-red-800"
                            : "bg-red-50 border border-red-100"
                          : theme === "dark"
                          ? "bg-slate-700/50"
                          : "bg-slate-50"
                      }`}>
                        <div className="flex items-center justify-between">
                          <p className={`text-sm font-medium ${
                            theme === "dark" ? "text-slate-300" : "text-slate-700"
                          }`}>Previous Surgeries</p>
                          {patientData?.previoussurgeries?.status === "true" ? (
                            <CheckCircleIcon className={theme === "dark" ? "text-red-400" : "text-red-500"} sx={{ fontSize: 18 }} />
                          ) : (
                            <CancelIcon className={theme === "dark" ? "text-slate-500" : "text-slate-300"} sx={{ fontSize: 18 }} />
                          )}
                        </div>
                        {patientData?.previoussurgeries?.status === "true" && patientData?.previoussurgeries?.note && (
                          <p className={`text-xs mt-1 ${
                            theme === "dark" ? "text-red-300" : "text-red-600"
                          }`}>Note: {patientData.previoussurgeries.note}</p>
                        )}
                      </div>
                      <div className={`rounded-lg p-3 ${
                        patientData?.covid193Doses?.status === "true"
                          ? theme === "dark"
                            ? "bg-red-900/30 border border-red-800"
                            : "bg-red-50 border border-red-100"
                          : theme === "dark"
                          ? "bg-slate-700/50"
                          : "bg-slate-50"
                      }`}>
                        <div className="flex items-center justify-between">
                          <p className={`text-sm font-medium ${
                            theme === "dark" ? "text-slate-300" : "text-slate-700"
                          }`}>COVID-19 (3 Doses)</p>
                          {patientData?.covid193Doses?.status === "true" ? (
                            <CheckCircleIcon className={theme === "dark" ? "text-red-400" : "text-red-500"} sx={{ fontSize: 18 }} />
                          ) : (
                            <CancelIcon className={theme === "dark" ? "text-slate-500" : "text-slate-300"} sx={{ fontSize: 18 }} />
                          )}
                        </div>
                        {patientData?.covid193Doses?.status === "true" && patientData?.covid193Doses?.note && (
                          <p className={`text-xs mt-1 ${
                            theme === "dark" ? "text-red-300" : "text-red-600"
                          }`}>Note: {patientData.covid193Doses.note}</p>
                        )}
                      </div>
                      <div className={`rounded-lg p-3 ${
                        patientData?.drugHistory?.status === "true"
                          ? theme === "dark"
                            ? "bg-red-900/30 border border-red-800"
                            : "bg-red-50 border border-red-100"
                          : theme === "dark"
                          ? "bg-slate-700/50"
                          : "bg-slate-50"
                      }`}>
                        <div className="flex items-center justify-between">
                          <p className={`text-sm font-medium ${
                            theme === "dark" ? "text-slate-300" : "text-slate-700"
                          }`}>Drug History</p>
                          {patientData?.drugHistory?.status === "true" ? (
                            <CheckCircleIcon className={theme === "dark" ? "text-red-400" : "text-red-500"} sx={{ fontSize: 18 }} />
                          ) : (
                            <CancelIcon className={theme === "dark" ? "text-slate-500" : "text-slate-300"} sx={{ fontSize: 18 }} />
                          )}
                        </div>
                        {patientData?.drugHistory?.status === "true" && patientData?.drugHistory?.note && (
                          <p className={`text-xs mt-1 ${
                            theme === "dark" ? "text-red-300" : "text-red-600"
                          }`}>Note: {patientData.drugHistory.note}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className={`text-sm font-semibold mb-3 ${
                      theme === "dark" ? "text-slate-300" : "text-slate-700"
                    }`}>Examination Notes</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className={`rounded-lg p-3 ${
                        theme === "dark" ? "bg-slate-700/50" : "bg-slate-50"
                      }`}>
                        <p className={`text-xs uppercase tracking-wide mb-1 ${
                          theme === "dark" ? "text-slate-400" : "text-slate-500"
                        }`}>Extra Oral Examination</p>
                        <p className={`text-sm font-medium ${
                          theme === "dark" ? "text-slate-200" : "text-slate-800"
                        }`}>{patientData?.extraOralExamination || "N/A"}</p>
                      </div>
                      <div className={`rounded-lg p-3 ${
                        theme === "dark" ? "bg-slate-700/50" : "bg-slate-50"
                      }`}>
                        <p className={`text-xs uppercase tracking-wide mb-1 ${
                          theme === "dark" ? "text-slate-400" : "text-slate-500"
                        }`}>Intra Oral Examination</p>
                        <p className={`text-sm font-medium ${
                          theme === "dark" ? "text-slate-200" : "text-slate-800"
                        }`}>{patientData?.intraOralExamination || "N/A"}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "examination" && (
                <div className="space-y-6">
                  <div>
                    <h4 className={`text-sm font-semibold mb-3 ${
                      theme === "dark" ? "text-slate-300" : "text-slate-700"
                    }`}>Treatments List</h4>
                    {loadingExamination ? (
                      <div className="flex items-center justify-center py-8">
                        <Circles height="30" width="30" color="#6366f1" ariaLabel="loading" />
                      </div>
                    ) : examinationData.length === 0 ? (
                      <div className={`text-center py-8 rounded-lg ${
                        theme === "dark" ? "bg-slate-700/50" : "bg-slate-50"
                      }`}>
                        <p className={theme === "dark" ? "text-slate-400" : "text-slate-500"}>No treatments found</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className={`border-b ${
                              theme === "dark" ? "border-slate-700" : "border-slate-200"
                            }`}>
                              <th className={`text-left py-2 px-3 text-xs font-semibold uppercase ${
                                theme === "dark" ? "text-slate-300" : "text-slate-700"
                              }`}>Date</th>
                              <th className={`text-left py-2 px-3 text-xs font-semibold uppercase ${
                                theme === "dark" ? "text-slate-300" : "text-slate-700"
                              }`}>Treatment</th>
                              <th className={`text-left py-2 px-3 text-xs font-semibold uppercase ${
                                theme === "dark" ? "text-slate-300" : "text-slate-700"
                              }`}>Note</th>
                              <th className={`text-left py-2 px-3 text-xs font-semibold uppercase ${
                                theme === "dark" ? "text-slate-300" : "text-slate-700"
                              }`}>Dentist</th>
                              <th className={`text-left py-2 px-3 text-xs font-semibold uppercase ${
                                theme === "dark" ? "text-slate-300" : "text-slate-700"
                              }`}>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {examinationData.map((item: any, i: number) => (
                              item?.treatments?.map((treatment: any, j: number) => (
                                <tr key={`${i}-${j}`} className={`border-b ${
                                  theme === "dark" ? "border-slate-700/50 hover:bg-slate-700/30" : "border-slate-100 hover:bg-slate-50"
                                } transition-colors`}>
                                  <td className={`py-2 px-3 text-xs ${
                                    theme === "dark" ? "text-slate-300" : "text-slate-600"
                                  }`}>
                                    {formatDate(treatment?.createdAt)}
                                  </td>
                                  <td className={`py-2 px-3 text-xs font-medium ${
                                    theme === "dark" ? "text-slate-200" : "text-slate-800"
                                  }`}>
                                    {treatment?.treatment}
                                    {treatment?.type ? ` - ${treatment?.type}` : ""}
                                    {treatment?.subtype ? ` - ${treatment?.subtype}` : ""}
                                  </td>
                                  <td className={`py-2 px-3 text-xs max-w-[150px] truncate ${
                                    theme === "dark" ? "text-slate-400" : "text-slate-500"
                                  }`}>
                                    {treatment?.note || "N/A"}
                                  </td>
                                  <td className={`py-2 px-3 text-xs ${
                                    theme === "dark" ? "text-slate-300" : "text-slate-600"
                                  }`}>
                                    {treatment?.didBy
                                      ? `${treatment?.didBy?.firstName || ""} ${treatment?.didBy?.lastName || ""}`.trim()
                                      : "N/A"}
                                  </td>
                                  <td className="py-2 px-3">
                                    <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${
                                      treatment?.status === "Complete"
                                        ? theme === "dark"
                                          ? "bg-emerald-900/30 text-emerald-300"
                                          : "bg-emerald-100 text-emerald-700"
                                        : treatment?.status === "ongoing"
                                        ? theme === "dark"
                                          ? "bg-amber-900/30 text-amber-300"
                                          : "bg-amber-100 text-amber-700"
                                        : theme === "dark"
                                        ? "bg-slate-700 text-slate-300"
                                        : "bg-slate-100 text-slate-600"
                                    }`}>
                                      {treatment?.status || "N/A"}
                                    </span>
                                  </td>
                                </tr>
                              ))
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === "invoices" && (
                <div className="space-y-6">
                  <div>
                    <h4 className={`text-sm font-semibold mb-3 ${
                      theme === "dark" ? "text-slate-300" : "text-slate-700"
                    }`}>Invoices</h4>
                    {loadingInvoices ? (
                      <div className="flex items-center justify-center py-8">
                        <Circles height="30" width="30" color="#6366f1" ariaLabel="loading" />
                      </div>
                    ) : invoicesData.length === 0 ? (
                      <div className={`text-center py-8 rounded-lg ${
                        theme === "dark" ? "bg-slate-700/50" : "bg-slate-50"
                      }`}>
                        <p className={theme === "dark" ? "text-slate-400" : "text-slate-500"}>No invoices found</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className={`border-b ${
                              theme === "dark" ? "border-slate-700" : "border-slate-200"
                            }`}>
                              <th className={`text-left py-2 px-3 text-xs font-semibold uppercase ${
                                theme === "dark" ? "text-slate-300" : "text-slate-700"
                              }`}>Invoice No</th>
                              <th className={`text-left py-2 px-3 text-xs font-semibold uppercase ${
                                theme === "dark" ? "text-slate-300" : "text-slate-700"
                              }`}>Patient</th>
                              <th className={`text-left py-2 px-3 text-xs font-semibold uppercase ${
                                theme === "dark" ? "text-slate-300" : "text-slate-700"
                              }`}>Total</th>
                              <th className={`text-left py-2 px-3 text-xs font-semibold uppercase ${
                                theme === "dark" ? "text-slate-300" : "text-slate-700"
                              }`}>Discount</th>
                              <th className={`text-left py-2 px-3 text-xs font-semibold uppercase ${
                                theme === "dark" ? "text-slate-300" : "text-slate-700"
                              }`}>Paid</th>
                              <th className={`text-left py-2 px-3 text-xs font-semibold uppercase ${
                                theme === "dark" ? "text-slate-300" : "text-slate-700"
                              }`}>Balance</th>
                              <th className={`text-left py-2 px-3 text-xs font-semibold uppercase ${
                                theme === "dark" ? "text-slate-300" : "text-slate-700"
                              }`}>Status</th>
                              <th className={`text-left py-2 px-3 text-xs font-semibold uppercase ${
                                theme === "dark" ? "text-slate-300" : "text-slate-700"
                              }`}>Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {invoicesData.map((item: any, i: number) => {
                              const paid = (item?.card || 0) + (item?.cash || 0);
                              const status = item?.due === 0 ? "Completed" : item?.due > 0 ? "Due" : "Paid";
                              return (
                                <tr key={i} className={`border-b ${
                                  theme === "dark" ? "border-slate-700/50 hover:bg-slate-700/30" : "border-slate-100 hover:bg-slate-50"
                                } transition-colors`}>
                                  <td className={`py-2 px-3 text-xs ${
                                    theme === "dark" ? "text-slate-300" : "text-slate-600"
                                  }`}>
                                    {item?.invoiceNo || "N/A"}
                                  </td>
                                  <td className={`py-2 px-3 text-xs ${
                                    theme === "dark" ? "text-slate-300" : "text-slate-600"
                                  }`}>
                                    {item?.fullName || "N/A"}
                                  </td>
                                  <td className={`py-2 px-3 text-xs font-medium ${
                                    theme === "dark" ? "text-slate-200" : "text-slate-800"
                                  }`}>
                                    Rs {item?.total || 0}
                                  </td>
                                  <td className={`py-2 px-3 text-xs ${
                                    theme === "dark" ? "text-slate-300" : "text-slate-600"
                                  }`}>
                                    Rs {item?.discount || 0}
                                  </td>
                                  <td className={`py-2 px-3 text-xs font-medium ${
                                    theme === "dark" ? "text-slate-200" : "text-slate-800"
                                  }`}>
                                    Rs {paid}
                                  </td>
                                  <td className={`py-2 px-3 text-xs font-medium ${
                                    theme === "dark" ? "text-slate-200" : "text-slate-800"
                                  }`}>
                                    Rs {item?.due || 0}
                                  </td>
                                  <td className="py-2 px-3">
                                    <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${
                                      status === "Completed"
                                        ? theme === "dark"
                                          ? "bg-emerald-900/30 text-emerald-300"
                                          : "bg-emerald-100 text-emerald-700"
                                        : status === "Due"
                                        ? theme === "dark"
                                          ? "bg-red-900/30 text-red-300"
                                          : "bg-red-100 text-red-700"
                                        : theme === "dark"
                                        ? "bg-blue-900/30 text-blue-300"
                                        : "bg-blue-100 text-blue-700"
                                    }`}>
                                      {status}
                                    </span>
                                  </td>
                                  <td className={`py-2 px-3 text-xs ${
                                    theme === "dark" ? "text-slate-300" : "text-slate-600"
                                  }`}>
                                    {item?.updatedAt ? formatDate(item.updatedAt.split("T")[0]) : "N/A"}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <p className={theme === "dark" ? "text-slate-400" : "text-slate-500"}>No patient data available</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PatientViewModal;

